import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SecurityActions } from "src/redux/Security/actions";
import { SecuritySelectors } from "src/redux/Security/selectors";
import { ConfirmActions } from "src/redux/Confirm/actions";
import useUnsavedChangesGuard from "src/utils/useUnsavedChangesGuard";
import Button from "src/components/common/Button";
import Ipv4Input from "./Ipv4Input";
import styles from "./styles.module.scss";

const REASON_MAXLEN = 256;

// Compose a Go duration from an amount + unit (days folded to hours); "" when blank.
const goDuration = (amt, unit) => {
    const s = String(amt || "").replace(/\D/g, "");
    if (!s) return "";
    const n = parseInt(s, 10);
    return unit === "d" ? `${n * 24}h` : `${n}${unit}`;
};

// A very long remaining ban duration is our "permanent" sentinel (CrowdSec decisions
// always carry an expiry).
const isPermanent = (d) => /^\d{5,}h/.test(d || "");
const banExpiry = (d) => (!d ? "-" : isPermanent(d) ? "Permanent" : d);

// Spaced CIDR in tables: "1.2.3.0/24" -> "1.2.3.0 / 24".
const fmtValue = (v) => (v && v.includes("/") ? v.replace("/", " / ") : v || "");

// Manual bans are the ones added here (cscli origin); everything else is auto-detected
// by CrowdSec (scenarios, community blocklist, imported lists).
const isManual = (b) => b.origin === "cscli";

/**
 * The Security tab: manage the CrowdSec blacklist (banned IPs) and whitelist (never-banned
 * allowlist) that gate the public counterparty ingress. Manual entries (added here) and
 * automated ones (detected by CrowdSec) are shown in separate tables. Edits are staged and
 * applied together from the bottom bar (behind the standard reason dialog, audited), like every
 * other admin page. Two reasons are kept separate: each entry's own reason (why this IP), and the
 * confirmation reason for the change itself.
 */
const Security = () => {
    const dispatch = useDispatch();
    const bans = useSelector(SecuritySelectors.Bans);
    const allowlist = useSelector(SecuritySelectors.Allowlist);
    const error = useSelector(SecuritySelectors.Error);

    const manualBans = bans.filter(isManual);
    const autoBans = bans.filter((b) => !isManual(b));

    // add-form inputs (same field order in both: IP, reason, then the time field)
    const [banIp, setBanIp] = useState("");
    const [banReason, setBanReason] = useState("");
    const [banDurAmt, setBanDurAmt] = useState("");
    const [banDurUnit, setBanDurUnit] = useState("h");
    const [allowIp, setAllowIp] = useState("");
    const [allowReason, setAllowReason] = useState("");
    const [allowTtlAmt, setAllowTtlAmt] = useState("");
    const [allowTtlUnit, setAllowTtlUnit] = useState("h");

    // staged changes (applied on confirm)
    const [banAdds, setBanAdds] = useState([]); // [{ip, duration, reason}]
    const [banRemoves, setBanRemoves] = useState([]); // [ip]
    const [allowAdds, setAllowAdds] = useState([]); // [{ip, ttl, reason}]
    const [allowRemoves, setAllowRemoves] = useState([]); // [ip]
    const [msg, setMsg] = useState(null);
    const [saving, setSaving] = useState(false);
    // Bumped after staging to remount (clear) the segmented IP + duration inputs.
    const [resetKey, setResetKey] = useState(0);

    const load = useCallback(() => {
        dispatch(SecurityActions.FetchBans());
        dispatch(SecurityActions.FetchAllowlist());
    }, [dispatch]);
    useEffect(() => {
        load();
    }, [load]);

    const canBlock = banIp.trim() && banReason.trim();
    const canAllow = allowIp.trim() && allowReason.trim();

    const stageBan = (e) => {
        e.preventDefault();
        if (!canBlock) return;
        setBanAdds((a) => [...a, { ip: banIp.trim(), duration: goDuration(banDurAmt, banDurUnit), reason: banReason.trim() }]);
        setBanIp("");
        setBanReason("");
        setBanDurAmt("");
        setBanDurUnit("h");
        setResetKey((k) => k + 1);
        setMsg(null);
    };
    const stageAllow = (e) => {
        e.preventDefault();
        if (!canAllow) return;
        setAllowAdds((a) => [...a, { ip: allowIp.trim(), ttl: goDuration(allowTtlAmt, allowTtlUnit), reason: allowReason.trim() }]);
        setAllowIp("");
        setAllowReason("");
        setAllowTtlAmt("");
        setAllowTtlUnit("h");
        setResetKey((k) => k + 1);
        setMsg(null);
    };

    const toggle = (setter) => (ip) => setter((r) => (r.includes(ip) ? r.filter((x) => x !== ip) : [...r, ip]));
    const toggleBanRemove = toggle(setBanRemoves);
    const toggleAllowRemove = toggle(setAllowRemoves);
    const unstage = (setter) => (i) => setter((a) => a.filter((_, j) => j !== i));
    const unstageBanAdd = unstage(setBanAdds);
    const unstageAllowAdd = unstage(setAllowAdds);

    const changes = [
        ...banAdds.map((b) => `Block ${b.ip}`),
        ...banRemoves.map((ip) => `Unban ${ip}`),
        ...allowAdds.map((a) => `Allow ${a.ip}`),
        ...allowRemoves.map((ip) => `Remove from allowlist ${ip}`),
    ];
    const dirty = changes.length > 0;
    const summary = changes.map((c) => "• " + c).join("\n");
    useUnsavedChangesGuard(dirty, summary);

    const discard = () => {
        setBanAdds([]);
        setBanRemoves([]);
        setAllowAdds([]);
        setAllowRemoves([]);
    };

    const confirm = () => {
        if (!dirty) return;
        const staged = { banAdds, banRemoves, allowAdds, allowRemoves, count: changes.length };
        dispatch(
            ConfirmActions.Open({
                title: "Apply security changes",
                message: "The following changes will be applied:\n" + summary,
                getDescription: true,
                isDescriptionRequired: true,
                descriptionPlaceholder: "Reason for this change",
                okTitle: "Apply",
                cancelTitle: "Cancel",
                onOk: async (auditReason) => {
                    setSaving(true);
                    setMsg(null);
                    try {
                        for (const b of staged.banAdds) {
                            await dispatch(SecurityActions.AddBan(b.ip, b.duration, b.reason, auditReason));
                        }
                        for (const ip of staged.banRemoves) {
                            await dispatch(SecurityActions.RemoveBan(ip, auditReason));
                        }
                        for (const a of staged.allowAdds) {
                            await dispatch(SecurityActions.AddAllowlistEntry(a.ip, a.reason, a.ttl, auditReason));
                        }
                        for (const ip of staged.allowRemoves) {
                            await dispatch(SecurityActions.RemoveAllowlistEntry(ip, auditReason));
                        }
                        discard();
                        load();
                        setMsg(`Saved ${staged.count} change${staged.count === 1 ? "" : "s"}.`);
                    } catch (e) {
                        setMsg("Failed to apply changes: " + (e.message || String(e)));
                    } finally {
                        setSaving(false);
                    }
                },
            })
        );
    };

    const renderRemoveCell = (ip, staged, toggleFn) => (
        <button
            className={staged ? styles.rowBtn + " " + styles.undoBtn : styles.rowBtn}
            data-testid={(staged ? "undo-" : "remove-") + ip}
            onClick={() => toggleFn(ip)}
        >
            {staged ? "Undo" : "Remove"}
        </button>
    );

    return (
        <div className={styles.security + (dirty ? " " + styles.hasStatusBar : "")} data-testid="security-view">
            <div className={styles.header}>
                <div className={styles.title}>Security</div>
                <div className={styles.intro}>
                    Manage the IP blacklist (banned) and whitelist (never banned) for the public counterparty ingress.
                    Addresses are IPv4 or IPv4 CIDR ranges; the whitelist always wins over a ban.
                </div>
            </div>

            {msg && (
                <div className={styles.actionMsg} data-testid="security-msg">
                    {msg}
                </div>
            )}
            {error && (
                <div className={styles.error} data-testid="security-error">
                    {error}
                </div>
            )}

            <section className={styles.section}>
                <div className={styles.sectionTitle}>Blacklist (blocked IPs)</div>
                <div className={styles.card}>
                    <div className={styles.subTitle}>Manual</div>
                    <form className={styles.addForm} onSubmit={stageBan}>
                        <Ipv4Input key={"ban-ip-" + resetKey} dataTestId="ban-ip" onChange={setBanIp} />
                        <input
                            className={styles.input + " " + styles.reasonInput}
                            data-testid="ban-reason"
                            placeholder="reason (required)"
                            maxLength={REASON_MAXLEN}
                            value={banReason}
                            onChange={(e) => setBanReason(e.target.value)}
                        />
                        <input
                            className={styles.durAmount}
                            inputMode="numeric"
                            placeholder="0"
                            data-testid="ban-duration"
                            value={banDurAmt}
                            onChange={(e) => setBanDurAmt(e.target.value.replace(/\D/g, "").slice(0, 5))}
                        />
                        <select
                            className={styles.durUnit}
                            data-testid="ban-duration-unit"
                            value={banDurUnit}
                            onChange={(e) => setBanDurUnit(e.target.value)}
                        >
                            <option value="m">minutes</option>
                            <option value="h">hours</option>
                            <option value="d">days</option>
                        </select>
                        <span className={styles.durHint}>blank = permanent</span>
                        <Button value="Block" dataTestId="ban-add" disabled={!canBlock} />
                    </form>
                    <table className={styles.table} data-testid="bans-table">
                        <thead>
                            <tr>
                                <th className={styles.colIp}>IP / range</th>
                                <th className={styles.colReason}>Reason</th>
                                <th className={styles.colTime}>Expires</th>
                                <th className={styles.colAction} />
                            </tr>
                        </thead>
                        <tbody>
                            {manualBans.length === 0 && banAdds.length === 0 && (
                                <tr>
                                    <td colSpan={4} className={styles.empty}>
                                        No manual bans
                                    </td>
                                </tr>
                            )}
                            {manualBans.map((b, i) => {
                                const removing = banRemoves.includes(b.value);
                                return (
                                    <tr key={(b.value || "") + "-" + i} className={removing ? styles.removingRow : ""}>
                                        <td className={styles.colIp}>{fmtValue(b.value)}</td>
                                        <td className={styles.colReason}>{b.scenario || "-"}</td>
                                        <td className={styles.colTime}>{banExpiry(b.duration)}</td>
                                        <td className={styles.colAction}>
                                            {renderRemoveCell(b.value, removing, toggleBanRemove)}
                                        </td>
                                    </tr>
                                );
                            })}
                            {banAdds.map((b, i) => (
                                <tr key={"add-" + i} className={styles.pendingRow}>
                                    <td className={styles.colIp}>{fmtValue(b.ip)}</td>
                                    <td className={styles.colReason}>
                                        {b.reason}
                                        <span className={styles.pendingTag}>pending</span>
                                    </td>
                                    <td className={styles.colTime}>
                                        {b.duration ? banExpiry(b.duration) : "Permanent"}
                                    </td>
                                    <td className={styles.colAction}>
                                        <button
                                            className={styles.rowBtn + " " + styles.undoBtn}
                                            data-testid={"ban-cancel-" + i}
                                            onClick={() => unstageBanAdd(i)}
                                        >
                                            Cancel
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className={styles.card + " " + styles.cardGap}>
                    <div className={styles.subTitle}>Automated</div>
                    <table className={styles.table} data-testid="auto-bans-table">
                        <thead>
                            <tr>
                                <th className={styles.colIp}>IP / range</th>
                                <th className={styles.colReason}>Reason</th>
                                <th className={styles.colTime}>Expires</th>
                                <th className={styles.colAction} />
                            </tr>
                        </thead>
                        <tbody>
                            {autoBans.length === 0 && (
                                <tr>
                                    <td colSpan={4} className={styles.empty}>
                                        No automated bans
                                    </td>
                                </tr>
                            )}
                            {autoBans.map((b, i) => (
                                <tr key={(b.value || "") + "-" + i}>
                                    <td className={styles.colIp}>{fmtValue(b.value)}</td>
                                    <td className={styles.colReason}>{b.scenario || "-"}</td>
                                    <td className={styles.colTime}>{banExpiry(b.duration)}</td>
                                    <td className={styles.colAction} />
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.sectionTitle}>Whitelist (never banned)</div>
                <div className={styles.card}>
                    <form className={styles.addForm} onSubmit={stageAllow}>
                        <Ipv4Input key={"allow-ip-" + resetKey} dataTestId="allow-ip" onChange={setAllowIp} />
                        <input
                            className={styles.input + " " + styles.reasonInput}
                            data-testid="allow-reason"
                            placeholder="reason (required)"
                            maxLength={REASON_MAXLEN}
                            value={allowReason}
                            onChange={(e) => setAllowReason(e.target.value)}
                        />
                        <input
                            className={styles.durAmount}
                            inputMode="numeric"
                            placeholder="0"
                            data-testid="allow-ttl"
                            value={allowTtlAmt}
                            onChange={(e) => setAllowTtlAmt(e.target.value.replace(/\D/g, "").slice(0, 5))}
                        />
                        <select
                            className={styles.durUnit}
                            data-testid="allow-ttl-unit"
                            value={allowTtlUnit}
                            onChange={(e) => setAllowTtlUnit(e.target.value)}
                        >
                            <option value="m">minutes</option>
                            <option value="h">hours</option>
                            <option value="d">days</option>
                        </select>
                        <span className={styles.durHint}>blank = never</span>
                        <Button value="Allow" dataTestId="allow-add" disabled={!canAllow} />
                    </form>
                    <table className={styles.table} data-testid="allowlist-table">
                        <thead>
                            <tr>
                                <th className={styles.colIp}>IP / range</th>
                                <th className={styles.colReason}>Reason</th>
                                <th className={styles.colTime}>Expires</th>
                                <th className={styles.colAction} />
                            </tr>
                        </thead>
                        <tbody>
                            {allowlist.length === 0 && allowAdds.length === 0 && (
                                <tr>
                                    <td colSpan={4} className={styles.empty}>
                                        No allowlist entries
                                    </td>
                                </tr>
                            )}
                            {allowlist.map((a, i) => {
                                const removing = allowRemoves.includes(a.value);
                                return (
                                    <tr key={(a.value || "") + "-" + i} className={removing ? styles.removingRow : ""}>
                                        <td className={styles.colIp}>{fmtValue(a.value)}</td>
                                        <td className={styles.colReason}>{a.description || "-"}</td>
                                        <td className={styles.colTime}>{a.expiration || "Never"}</td>
                                        <td className={styles.colAction}>
                                            {renderRemoveCell(a.value, removing, toggleAllowRemove)}
                                        </td>
                                    </tr>
                                );
                            })}
                            {allowAdds.map((a, i) => (
                                <tr key={"add-" + i} className={styles.pendingRow}>
                                    <td className={styles.colIp}>{fmtValue(a.ip)}</td>
                                    <td className={styles.colReason}>
                                        {a.reason}
                                        <span className={styles.pendingTag}>pending</span>
                                    </td>
                                    <td className={styles.colTime}>{a.ttl || "Never"}</td>
                                    <td className={styles.colAction}>
                                        <button
                                            className={styles.rowBtn + " " + styles.undoBtn}
                                            data-testid={"allow-cancel-" + i}
                                            onClick={() => unstageAllowAdd(i)}
                                        >
                                            Cancel
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {dirty && (
                <div className={styles.statusBar} data-testid="security-statusbar">
                    <span>
                        {changes.length} unsaved change{changes.length > 1 ? "s" : ""}
                    </span>
                    <Button
                        value="Discard Changes"
                        onClick={discard}
                        disabled={saving}
                        classes={[styles.detailBtn]}
                        dataTestId="security-discard"
                    />
                    <Button
                        value={saving ? "Saving..." : "Confirm Changes"}
                        onClick={confirm}
                        disabled={saving}
                        classes={[styles.removeBtn]}
                        dataTestId="security-confirm"
                    />
                </div>
            )}
        </div>
    );
};

export default Security;
